import React, { useEffect, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { Loader2, IndianRupee, ShoppingBag, Users, Factory, Package, Printer, Truck } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";

const Stat = ({ label, value, icon: Icon, testId }) => (
    <div className="rounded-2xl bg-card border p-5" data-testid={testId}>
        <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center"><Icon className="w-5 h-5" /></div>
            <div>
                <div className="text-xs text-muted-foreground uppercase tracking-widest">{label}</div>
                <div className="font-display text-2xl font-bold">{value}</div>
            </div>
        </div>
    </div>
);

const AdminDashboard = () => {
    const [data, setData] = useState(null);
    useEffect(() => { (async () => { const { data } = await api.get("/admin/dashboard"); setData(data); })(); }, []);
    if (!data) return <div className="p-10"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

    return (
        <div className="p-8" data-testid="admin-dashboard">
            <h1 className="font-display text-3xl font-bold tracking-tight">Dashboard</h1>
            <p className="text-sm text-muted-foreground">Live overview of Cherishables business.</p>

            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat testId="stat-total-revenue" label="Total Revenue" value={formatINR(data.revenue.total_revenue)} icon={IndianRupee} />
                <Stat label="Product Revenue" value={formatINR(data.revenue.product_revenue)} icon={IndianRupee} />
                <Stat label="Paid Orders" value={data.totals.paid_orders} icon={ShoppingBag} />
                <Stat label="Today's Orders" value={data.totals.todays_orders} icon={ShoppingBag} />
            </div>

            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat label="All Orders" value={data.totals.orders} icon={ShoppingBag} />
                <Stat label="Customers" value={data.totals.customers} icon={Users} />
                <Stat label="Leads" value={data.totals.leads} icon={Users} />
                <Stat label="Pending" value={data.queues.pending_orders} icon={Factory} />
            </div>

            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat label="Production" value={data.queues.production_queue} icon={Factory} />
                <Stat label="Printing" value={data.queues.printing_queue} icon={Printer} />
                <Stat label="Shipping" value={data.queues.shipping_queue} icon={Truck} />
                <Stat label="Delivered (period)" value={data.queues.shipping_queue} icon={Package} />
            </div>

            <div className="mt-6 rounded-2xl bg-card border p-5">
                <h3 className="font-display text-lg font-semibold mb-3">Revenue (last 14 days)</h3>
                <div className="h-72">
                    <ResponsiveContainer>
                        <LineChart data={data.revenue_by_day}>
                            <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                            <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} />
                            <Tooltip formatter={(v) => formatINR(v)} />
                            <Line type="monotone" dataKey="revenue" stroke="hsl(var(--primary))" strokeWidth={3} dot={{ r: 3 }} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
};

export default AdminDashboard;
